import React from "react";

import { ScheduleDayDto, ScheduleDto } from "services/menu/restModels";

import { parseTimeString, toTimeString } from "shared/lib/timeSlot";
import { TimeField, ToggleSwitch } from "shared/ui";

import { Dash, DayRow, DaySwitch, RangeField, TimeRange } from "./styles";

type DayKey =
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday";

const DAYS: { key: DayKey; label: string }[] = [
    { key: "monday", label: "Понедельник" },
    { key: "tuesday", label: "Вторник" },
    { key: "wednesday", label: "Среда" },
    { key: "thursday", label: "Четверг" },
    { key: "friday", label: "Пятница" },
    { key: "saturday", label: "Суббота" },
    { key: "sunday", label: "Воскресенье" },
];

const EMPTY_SLOT = { fromHour: 0, toHour: 0, fromMinute: 0, toMinute: 0 };

interface ScheduleEditorProps {
    schedule: ScheduleDto;
    onChange: (schedule: ScheduleDto) => void;
}

/*
* Понедельная сетка «день → интервал». Prop-driven и живёт отдельно от вкладки, потому что
* одинаковое расписание правится в двух местах — у позиции и у категории меню.
* Тумблер «включить расписание» остаётся за вызывающим: у вкладки он в шапке карточки,
* у модалки категории — в строке формы.
* */
export const ScheduleEditor = ({ schedule, onChange }: ScheduleEditorProps) => {
    const updateDay = (key: DayKey, day: ScheduleDayDto) =>
        onChange({ ...schedule, [key]: day });

    return (
        <>
            {DAYS.map(({ key, label }) => {
                const day = schedule[key];
                const slot = day.slots[0] || EMPTY_SLOT;

                const setDayEnabled = (isEnabled: boolean) =>
                    updateDay(key, { ...day, isEnabled });

                const setFrom = (value: string) => {
                    const parsed = parseTimeString(value);
                    if (!parsed) return;
                    updateDay(key, {
                        ...day,
                        slots: [{ ...slot, fromHour: parsed.hour, fromMinute: parsed.minute }],
                    });
                };

                const setTo = (value: string) => {
                    const parsed = parseTimeString(value);
                    if (!parsed) return;
                    updateDay(key, {
                        ...day,
                        slots: [{ ...slot, toHour: parsed.hour, toMinute: parsed.minute }],
                    });
                };

                return (
                    <DayRow key={key}>
                        <DaySwitch>
                            <ToggleSwitch
                                checked={day.isEnabled}
                                onChange={setDayEnabled}
                                label={label}
                            />
                        </DaySwitch>
                        <TimeRange>
                            <RangeField>
                                <TimeField
                                    value={toTimeString(slot.fromHour, slot.fromMinute)}
                                    disabled={!day.isEnabled}
                                    onChange={setFrom}
                                />
                            </RangeField>
                            <Dash>—</Dash>
                            <RangeField>
                                <TimeField
                                    value={toTimeString(slot.toHour, slot.toMinute)}
                                    disabled={!day.isEnabled}
                                    onChange={setTo}
                                />
                            </RangeField>
                        </TimeRange>
                    </DayRow>
                );
            })}
        </>
    );
};
